import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import SettingsPage from '../../partials/SettingsPage';
import LoadingSpinner from '../../partials/LoadingSpinner';
import PageHeader from '../../partials/PageHeader';
import SuggestionItemsModal from './SuggestionItemsModal';

import { Button, Text, Small, Panel, H3, H4, Badge, Box, Dropdown, Tabs, Select } from '@bigcommerce/big-design';
import { ArrowDropDownIcon, ChevronLeftIcon, ChevronRightIcon, VisibilityIcon, AutoAwesomeIcon } from '@bigcommerce/big-design-icons';

import { locationsIndex } from '../../../redux/actions/locations';
import { suppliersIndex } from '../../../redux/actions/suppliers';
import { createSuggestedPurchaseOrder, suggestedPurchaseOrdersIndex, findSuggestedPurchaseOrder } from '../../../redux/actions/suggestedPurchaseOrders';

const Suggestions = ({ payload, locations, suppliers, suggestedPurchaseOrders, loadLocations, loadSuppliers, loadSuggestions, loadSuggestedOrder, newSuggestion }) => {
  const [showCreateSuggestionModal, setShowCreateSuggestionModal] = useState(false);
  const [activeAddress, setActiveAddress] = useState(null);
  const [activeSupplier, setActiveSupplier] = useState(null);
  const [showSuggestionItemsModal, setShowSuggestionItemsModal] = useState(false);
  const [existsError, setExistsError] = useState(null);

  const [page, setPage] = useState(0);

  useEffect(() => {
    loadSuppliers(payload);
    loadLocations(payload);
    loadNextSuggestedPurchaseOrders();
  }, []);


  const showItemsModal = (suggestedPurchaseOrderId) => {
    loadSuggestedOrder(payload, suggestedPurchaseOrderId);
    setShowSuggestionItemsModal(true);
  }

  const createSuggestion = () => {
    newSuggestion(payload, activeSupplier.id, activeAddress.id);
  }

  const checkExisting = (location, supplier) => {
    let currentDate = new Date();
    const offset = currentDate.getTimezoneOffset();
    let existing = suggestedPurchaseOrders.suggestedPurchaseOrders.find(spo => (spo.location_id === location?.id) && (spo.supplier_id === supplier?.id) && (spo.suggestion_date === (new Date(currentDate.getTime() - (offset*60*1000)).toISOString().split('T')[0])) )

    setExistsError(!!existing);
  }

  const activateAddress = (location) => {
    setActiveAddress(location);
    checkExisting(location, activeSupplier);
  }

  const activateSupplier = (supplier) => {
    setActiveSupplier(supplier);
    checkExisting(activeAddress, supplier);
  }

  const locationItems = () => {
    let items = [];

    locations.locations.map(location => 
      items.push(
        {
          content: location.label,
          onItemClick: () => activateAddress(location),
          hash: location.id
        }
      )
    );

    return items;
  }

  const supplierSelectItems = () => {
    let items = [];

    suppliers.suppliers.map(supplier => 
      items.push(
        {
          value: supplier,
          content: supplier.supplier_name
        }
      )
    )

    return items;
  }


  const loadPreviousSuggestedPurchaseOrders = () => {
    let previousContinuationToken = suggestedPurchaseOrders.purchaseOrdersSearchStack[page - 2];
    loadSuggestions(payload, previousContinuationToken, (page - 1));

    setPage(page - 1);
  }

  const loadNextSuggestedPurchaseOrders = () => {
    loadSuggestions(payload, suggestedPurchaseOrders.purchaseOrdersSearchStack[page], (page + 1));
    setPage(page + 1);
  }


  const description = () => {
    return(
      <> 
        <Text>Select a location and supplier to generate a suggested purchase order based on thresholds set in the "Set Thresholds" tool.</Text>
        <Text>Only one suggestion can be generated per day, per location and supplier combination. Suggestions are kept for 2 days before being removed.</Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  return(
    <>
      <PageHeader stripeBackground='#5bc6cc' helpLink="bigcommerce/manage-suggested-purchase-orders/" />
      <SettingsPage title="Suggested Purchase Orders" description={description()}>
        <Panel>
          <H3>Suggest New Purchase Order</H3>

          <div className="flex-container">
            <div className="flex-1">
              { locations.status === 'success' &&
                <div className="flex-1">
                  <H4>Location</H4>
                  <Dropdown
                    items={locationItems()}
                    maxHeight={250}
                    placement="bottom-start"
                    toggle={<Button iconRight={<ArrowDropDownIcon />}>{activeAddress?.label || "Location"}</Button>}
                  />
                </div>
              }
              { locations.status !== 'success' &&
                <LoadingSpinner statusText="Loading locations" />
              }
            </div>

            <div className="flex-1">
              { suppliers.status === 'success' &&
                <div className="flex-1">
                  <H4>Supplier</H4>
                  <Select
                    filterable={true}
                    label="Suppliers"
                    maxHeight={300}
                    onOptionChange={(supplier) => activateSupplier(supplier)}
                    options={supplierSelectItems()}
                    placeholder="Select Supplier"
                    placement="bottom-start"
                    required
                    value={activeSupplier?.supplier_name}
                  />
                </div>
              }
              { suppliers.status !== 'success' &&
                <LoadingSpinner statusText="Loading suppliers" />
              }
            </div>
          </div>

          <hr/>

          <Button
            disabled={!(activeAddress && activeSupplier) || suggestedPurchaseOrders.isUpdating || existsError}
            isLoading={suggestedPurchaseOrders.isUpdating}
            onClick={() => createSuggestion()}
            iconLeft={<AutoAwesomeIcon />}
          >
            Suggest Purchase Order
          </Button>

          { existsError && 
            <Text color='warning'>
              A suggested purchase order already exists for this supplier and location for today.
            </Text>
          }

          { suggestedPurchaseOrders.errors &&
            <Text color='danger'>
              {suggestedPurchaseOrders.errors}
            </Text>
          }

          { !activeAddress &&
            <Text color="danger70">
              Select a BigCommerce location to suggest a purchase order
            </Text>
          }

          { !activeSupplier &&
            <Text color="danger70">
              Select a supplier to suggest purchase order
            </Text>
          }

        </Panel>

        <Panel>
          <H3>Suggested Purchase Orders</H3>
          { !(suppliers.status === 'success' && !suggestedPurchaseOrders.isFetching && locations.status === 'success') &&
            <LoadingSpinner statusText="Loading suppliers" />
          }
          { (suppliers.status === 'success' && !suggestedPurchaseOrders.isFetching && locations.status === 'success') &&
            <>
              <div className="flex-container">
                <div className="flex-1">
                  <Button
                    onClick={() => loadPreviousSuggestedPurchaseOrders()}
                    disabled={page === 1}
                    iconLeft={<ChevronLeftIcon />}
                    variant="secondary"
                  >
                    Previous Suggested Purchase Orders
                  </Button>

                  <Button
                    onClick={() => loadNextSuggestedPurchaseOrders()}
                    disabled={suggestedPurchaseOrders.lastPage}
                    iconRight={<ChevronRightIcon />}
                    variant="secondary"
                  >
                    Next Suggested Purchase Orders
                  </Button>
                </div>
              </div>
              <br/>

              { suggestedPurchaseOrders.isFetching &&
                <LoadingSpinner statusText="Loading Purchase Orders" />
              }

              { suggestedPurchaseOrders.suggestedPurchaseOrders.length === 0 && !suggestedPurchaseOrders.isFetching &&
                <Box
                  backgroundColor="warning10"
                  border="box"
                  borderRadius="normal"
                  padding="medium"
                >
                  <div className="text-center">
                    <Text>No outstanding suggested purchase orders. Create suggested purchase orders to see them in a list here.</Text>
                  </div>
                </Box>
              }

              { suggestedPurchaseOrders.suggestedPurchaseOrders.map(suggestedPurchaseOrder =>
                <Panel>
                  <H3>{suggestedPurchaseOrder.suggestion_date} - {locations?.locations?.find(loc => loc.id === suggestedPurchaseOrder.location_id)?.label} - {suppliers?.suppliers?.find(supp => supp.id === suggestedPurchaseOrder.supplier_id)?.supplier_name}</H3>
                  <div className="flex-container">
                    <div className="flex-1">
                      <ul>
                        <li><strong>Suggestion Date</strong>: {suggestedPurchaseOrder.suggestion_date}</li>
                        <li><strong>BigCommerce Location</strong>: {locations?.locations?.find(loc => loc.id === suggestedPurchaseOrder.location_id)?.label}</li>
                        <li><strong>Supplier</strong>: {suppliers?.suppliers?.find(supp => supp.id === suggestedPurchaseOrder.supplier_id)?.supplier_name}</li>
                      </ul>
                    </div>
                    <div className="flex-1">
                      <strong>Status</strong>: <Badge label={suggestedPurchaseOrder.status} variant={'success'}/>
                      { suggestedPurchaseOrder.status !== 'ready' && suggestedPurchaseOrder.status !== 'complete' && suggestedPurchaseOrder.status !== 'empty' &&
                        <Button variant='utility' onClick={() => loadSuggestions(payload, suggestedPurchaseOrders.purchaseOrdersSearchStack[page - 1], page)}>Refresh Status</Button>
                      }

                      { suggestedPurchaseOrder.status === 'ready' &&
                        <>
                          <br/>
                          <Button
                            variant='utility'
                            iconLeft={<VisibilityIcon />}
                            onClick={() => showItemsModal(suggestedPurchaseOrder.id)}
                          >
                            View Suggested Order Items
                          </Button>
                          <br/>

                          <Link to={`/purchase_order/suggestion/${suggestedPurchaseOrder.id}`}>
                            <Button
                              variant='secondary'
                            >
                              + Create Suggested Purchase Order
                            </Button>
                          </Link>
                        </>
                      }
                    </div>
                  </div>
                </Panel>
              )}
            </>
          }

        </Panel>
      </SettingsPage>

      <SuggestionItemsModal
        payload={payload}
        showSuggestionItemsModal={showSuggestionItemsModal}
        setShowSuggestionItemsModal={setShowSuggestionItemsModal}
        suggestedPurchaseOrder={suggestedPurchaseOrders.activeSuggestedPurchaseOrder}
      />
    </>
  )

}

const mapStateToProps = state => ({
  locations: state.locations,
  suppliers: state.suppliers,
  suggestedPurchaseOrders: state.suggestedPurchaseOrders
});

const mapDispatchToProps = (dispatch) => ({
  loadLocations: (payload) => dispatch(locationsIndex(payload)),
  loadSuppliers: (payload) => dispatch(suppliersIndex(payload)),
  loadSuggestions: (payload, continuationToken, page) => dispatch(suggestedPurchaseOrdersIndex(payload, continuationToken, page)),
  loadSuggestedOrder: (payload, suggestedOrderId) => dispatch(findSuggestedPurchaseOrder(payload, suggestedOrderId)),
  newSuggestion: (payload, supplier, address) => dispatch(createSuggestedPurchaseOrder(payload, supplier, address))
});

export default connect(mapStateToProps, mapDispatchToProps)(Suggestions);


